// Governed metric pairings for the dual-axis trend.
//
// A two-axis chart is the easiest chart in the world to lie with: put any two
// series on independent scales and the eye invents a relationship. So the
// pairings are declared, not inferred — a fixed list of pairs that a finance
// reader would genuinely want side by side, each with its axis assignment and
// the reason it is worth showing.
//
// Two rules the list enforces:
//
//  - **No definitional pairs.** Revenue against gross profit looks like a
//    striking correlation and carries no information: gross profit is revenue
//    minus cost, so they move together by construction. Pairs must be able to
//    diverge, and the divergence must mean something.
//  - **Different units, or don't bother.** Two dollar series belong on one axis
//    in a normal trend chart. The second axis earns its place only when the
//    partner is a rate, a ratio, or a duration.

export type MetricAxis = 'primary' | 'secondary'

export type MetricPair = {
  /** Metric id of the primary (left-axis) series. */
  primary: string
  /** Metric id of the secondary (right-axis) series. */
  secondary: string
  primaryUnit: string
  secondaryUnit: string
  /** What a reader learns when the two diverge. Rendered as the chart's subtitle. */
  divergenceMeans: string
}

/**
 * The declared pairs. Ordered: the first pair whose primary matches a card's
 * metric wins, so put the most decision-relevant partner first for any metric
 * that appears more than once.
 */
export const GOVERNED_METRIC_PAIRS: readonly MetricPair[] = [
  {
    primary: 'revenue',
    secondary: 'gross_margin',
    primaryUnit: 'usd',
    secondaryUnit: 'pct',
    divergenceMeans: 'Revenue growing while margin falls means the added volume is being bought.',
  },
  {
    primary: 'accounts_receivable',
    secondary: 'days_sales_outstanding',
    primaryUnit: 'usd',
    secondaryUnit: 'days',
    divergenceMeans: 'A balance rising faster than DSO is growth; DSO rising faster is a collections problem.',
  },
  {
    primary: 'cash',
    secondary: 'net_income',
    primaryUnit: 'usd',
    secondaryUnit: 'usd',
    divergenceMeans: 'Profit without cash means the earnings are sitting in working capital.',
  },
  {
    primary: 'operating_expenses',
    secondary: 'operating_margin',
    primaryUnit: 'usd',
    secondaryUnit: 'pct',
    divergenceMeans: 'Spend rising without margin falling means the spend is carrying its weight.',
  },
  {
    primary: 'net_income',
    secondary: 'net_margin',
    primaryUnit: 'usd',
    secondaryUnit: 'pct',
    divergenceMeans: 'Absolute profit and profitability diverge when the revenue base is moving underneath.',
  },
]

/**
 * Pairs that must never be offered, even if someone adds them above.
 *
 * Each of these is an accounting identity: one series is defined in terms of the
 * other, so the chart would show a correlation the reader already knows and
 * imply an insight that is not there.
 */
const DEFINITIONAL_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['revenue', 'gross_profit'],
  ['revenue', 'net_income'],
  ['gross_profit', 'net_income'],
  ['revenue', 'cost_of_sales'],
  ['gross_profit', 'cost_of_sales'],
]

const norm = (value: unknown): string =>
  String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')

export function isDefinitionalPair(a: string, b: string): boolean {
  const left = norm(a)
  const right = norm(b)
  return DEFINITIONAL_PAIRS.some(([x, y]) => (x === left && y === right) || (x === right && y === left))
}

/** The declared partner for a metric, or null when the metric has none. */
export function pairFor(metricId: string): MetricPair | null {
  const target = norm(metricId)
  const found = GOVERNED_METRIC_PAIRS.find((pair) => norm(pair.primary) === target)
  if (!found) return null
  // Belt and braces: a definitional pair must not survive even if declared.
  if (isDefinitionalPair(found.primary, found.secondary)) return null
  return found
}
